import { autoinject, BindingEngine, observable, transient } from "aurelia-framework";
import { I18N } from "aurelia-i18n";
import { Claims } from "../../../../constants/authentication/claims";
import { RightShipDialogServiceConstants } from "../../../../constants/basics/rightship-dialog.constants";
import { MobileHelper } from "../../../../helpers/mobile-helper";
import { DeleteCustomReportRequest as DeleteCustomReportsRequest } from "../../../../models/custom-reports/custom-reports.models";
import { DataType, QueryFilter, SourceType } from "../../../../models/queries/queries.models";
import { ValidationResult } from "../../../../models/validations/validations.models";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";
import { CustomReportService } from "../../../../services/custom-report/custom-report.service";
import { ValidationsClientService } from "../../../../services/validations/validations-client.service";
import { Guid } from "../../../../utilities/guid";
import { CustomGridPageBase } from "../../common/custom-grid-page.base";
import KeyDownEvent = JQuery.KeyDownEvent;
import * as moment from "moment";

@autoinject()
@transient()
export class CustomReports extends CustomGridPageBase {

    @observable()
    filter: string;
    appliedFilter: string;

    filterKeyDown($event) {
        const e: KeyDownEvent = $event.detail;

        if (e.key == "Enter") {
            console.log(this.filter)
            this.appliedFilter = this.filter;
            this.reloadFilters();
        }
    }

    filterChanged() {
        if ((this.filter && this.filter.length >= 5) || this.filter == "") {
            this.appliedFilter = this.filter;
            this.reloadFilters();
        }
    }
    
    public canAdd: boolean;
    public canRemove: boolean;
    public selectionMode: string = "none";
    public isExporting: boolean = false;

    constructor(i18N: I18N, protected bindingEngine: BindingEngine, private userClaimsService: UserClaimsService,
        private dialogClient: DialogClientService,
        private validationsClientService: ValidationsClientService,
        private notificationClientService: NotificationClientService,
        private customReportService: CustomReportService) {

        super(i18N, 'Custom Reports', bindingEngine);

        this.canAdd = this.userClaimsService.hasPermission(Claims.CanAddCustomReports);
        this.canRemove = this.userClaimsService.hasPermission(Claims.CanDeleteCustomReports);

        const columns = [];
        columns.push({
            dataType: DataType.CustomTemplate,
            value: `<check-box checked.bind="getRowItem() && getRowItem().isSelected"
                        changed.delegate="getDataGrid().selectRowInMultipleSelection(getRowItem())">
                    </check-box>`,
            name: "select",
            title: MobileHelper.isMobileDevice() ? "" : `<check-box class="check-box-select-all" checked.two-way="toggleAll"></check-box>`,
            parameter: {
                item: null
            },
            instancePerRow: true,
            sortOrder: 1,
            columnOrder: -2,
            width: "50px",
            isHidden: false,
            isSortable: false,
            getCellClass: () => {
                return "selector-checkbox";
            }
        },
            {
                columnOrder: 0,
                sortOrder: 0,
                dataType: DataType.CustomTemplate,
                isSortable: true,
                instancePerRow: true,
                name: 'name',
                title: 'Name',
                value: '<a class="link" href="/custom-report/${item.customReportId}">${item.name}</a>',
                parameter: {
                    item: null,
                }
            },
            {
                sortOrder: 0,
                columnOrder: 0,
                dataType: DataType.Default,
                isSortable: true,
                name: 'customReportType',
                title: 'Report Type'
            },
            {
                sortOrder: 0,
                columnOrder: 0,
                dataType: DataType.Default,
                isSortable: true,
                name: 'owner',
                title: 'Owner'
            },
            {
                sortOrder: -1,
                isDescendingOnFirstSortingTriggered: true,
                columnOrder: 0,
                dataType: DataType.DateTime,
                isSortable: true,
                name: 'lastModifiedDate',
                title: 'Last Modified Date',
                valueConverter: (value) => moment(value).format("DD MMM YYYY")
            },
            {
                sortOrder: 0,
                columnOrder: 0,
                dataType: DataType.Default,
                isSortable: true,
                name: 'lastModifiedBy',
                title: 'Last Modified By'
            },
            {
                columnOrder: 0,
                sortOrder: 0,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: "menu",
                value: `<div class="actions row justify-content-end align-items-middle">
                            <list-menu hide-menu-border="true"  menu-items.bind="menuItems(item)">
                                <template replace-part="menu-item">
                                    <div class="row nowrap align-items-middle" click.trigger="menuItem.click($event, item)">
                                        <span class="text">\${menuItem.item.text}</span>
                                    </div>  
                                </template>
                            </list-menu>
                        </div>`,
                parameter: {
                    item: null,
                    menuItems: () => {
                        return [
                            {
                                isVisible: true,
                                click: ($event, item) => {
                                    if (!item) {
                                        return;
                                    }
                                    this.dialogClient.show(RightShipDialogServiceConstants.URLs.ExportCustomReportDialog, {
                                        onConfirm: () => {
                                            this.isExporting = true;
                                            this.customReportService.exportCustomReport(item.customReportId).then(() => {
                                                this.isExporting = false;
                                            }).catch(() => {
                                                this.isExporting = false;
                                            });
                                        } 
                                    }, false, false, false, "right");
                                },
                                item: { text: "Export" },
                                classList: ''
                            },
                            {
                                isVisible: () =>
                                    this.userClaimsService.hasPermission(Claims.CanUpdateCustomReports),
                                click: ($event, item) => {
                                    if ($event) {
                                        $event.stopPropagation();
                                    }
                                    this.editCustomReport(item);
                                },
                                item: { text: "Edit" },
                                classList: ''
                            },
                            {
                                isVisible: () =>
                                    this.userClaimsService.hasPermission(Claims.CanDeleteCustomReports),
                                click: ($event, item) => {
                                    if ($event) {
                                        $event.stopPropagation();
                                    }
                                    this.showDeleteDialog([item]);
                                },
                                item: { text: "Remove" },
                                classList: ''
                            }
                        ]
                    }
                }
            });

        columns.forEach((column, index) => {
            column.columnOrder = index;
            column.sortOrder = index;
        });

        this.columns = columns;
        this.itemSource = {
            sourceType: SourceType.ODataUrl,
            source: "odata/CustomReports",
            columns: this.columns,
            initialFilters: []
        };
    }

    addCustomReport() {
        this.dialogClient.show(RightShipDialogServiceConstants.URLs.EditCustomReportDialog, {
            onComplete: () => {
                this.reloadFilters();
            },
            id: null
        }, true, false, false, 'right');
    }

    editCustomReport(item) {
        this.dialogClient.show(RightShipDialogServiceConstants.URLs.EditCustomReportDialog, {
            onComplete: () => {
                this.reloadFilters();
            },
            id: item ? item.id : null
        }, true, false, false, 'right');
    }

    showDeleteDialog(items: any[]) {
        items = items || this.dataGridVM.getSelectedRowsCollection().map(x => x.item);
        this.dialogClient.show(RightShipDialogServiceConstants.URLs.CustomReportDeleteDialog, { items: items, deleteFunc: this.deleteCustomReport }, true, false, false, 'right');
    }

    deleteCustomReport = (items: any[], reason: string) => {

        return new Promise<void>((resolve, reject) => {

            const request: DeleteCustomReportsRequest = {
                ids: items.map(x => x.customReportId),
                reason: reason
            }

            this.customReportService.deleteCustomReports(request).then(response => {
                if (response.validationResult && response.validationResult.hasError) {
                    this.validationsClientService.showValidationErrors(response.validationResult);
                    reject();
                } else {
                    this.notificationClientService.show(this.i18N.tr('successfully-deleted-text'), "", NotificationType.Success, 5000);
                    this.reloadFilters();
                    resolve();
                }
            }).catch(x => {
                const response: {
                    exception: string;
                    details: string;
                    validationResult: ValidationResult;
                } = typeof (x.response) === 'object'
                        ? x.response
                        : (x.response ? JSON.parse(x.response) : {});
                if (!response.exception || !response.details) {
                    this.notificationClientService.show(this.i18N.tr('failed-custom-report-deletion-text'), "", NotificationType.Error, 5000);
                } else if (response.validationResult && response.validationResult.hasError) {
                    this.validationsClientService.showValidationErrors(response.validationResult);
                }
                reject();
            });
        })
    };

    reloadFilters() {
        const initialFilters = [];

        if (this.appliedFilter) {
            const guid = Guid.generateGuid();
            initialFilters.push(QueryFilter.getQuery("owner", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, true, false, guid, true, true));
        }

        this.itemSource = {
            sourceType: SourceType.ODataUrl,
            source: "odata/CustomReports",
            columns: this.columns,
            initialFilters: initialFilters,
            initialSorting: this.initialSorting
        };
    }
}